import 'reflect-metadata';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { distinct, filter, map, toArray } from 'rxjs/operators';
import { FeaturesTreeReader, FeaturesTreeReaderFactory, UTCDate, memoize } from 'types';
import { ValidationSeveritySetting } from 'validation';
import { DocumentField, MaskedDate, NullableMaskedDate, ArrayColumnSetter, Enum } from './features-decl';
import {
  MaskedDateItem,
  FeaturesImpl,
  Calculator,
  Column,
  StringItem,
  DocumentFieldInfo,
  VisibleItem,
  getPath,
  ValidationSpecification,
  EnumItem,
  ArrayInfo,
} from './features-impl';
import { Transliterator } from '../translit/transliterator';
import { TransliterationMethod } from '../translit/transliteration';

class FeaturesTreeReaderImpl extends FeaturesTreeReader {
  readTree(src: any, dst: any): void {
    FeaturesImpl.readTree(src, dst);
  }
}

@Injectable()
export class FeaturesTreeReaderFactoryImpl extends FeaturesTreeReaderFactory {
  create(): FeaturesTreeReader {
    return new FeaturesTreeReaderImpl();
  }
}

function reflect(parentPrototype: any, propertyKey: string, childPrototype: any) {
  FeaturesImpl.visibleItems
    .filter((it) => it.rootPrototype === childPrototype)
    .forEach((it) => {
      const visibleItem = new VisibleItem();
      visibleItem.rootPrototype = parentPrototype;
      visibleItem.nodePrototype = it.nodePrototype;
      visibleItem.targetProperty = propertyKey + '.' + it.targetProperty;
      visibleItem.targetVisibilityLambda = it.targetVisibilityLambda;
      FeaturesImpl.visibleItems.push(visibleItem);
    });
  FeaturesImpl.maskedDateItems
    .filter((it) => it.rootPrototype === childPrototype)
    .forEach((it) => {
      const maskedDateItem = new MaskedDateItem();
      maskedDateItem.rootPrototype = parentPrototype;
      maskedDateItem.targetProperty = propertyKey + '.' + it.targetProperty;
      maskedDateItem.dmy = it.dmy;
      maskedDateItem.my = it.my;
      maskedDateItem.y = it.y;
      maskedDateItem.maxDate = it.maxDate;
      FeaturesImpl.maskedDateItems.push(maskedDateItem);
    });
  FeaturesImpl.enumItems
    .filter((it) => it.rootPrototype === childPrototype)
    .forEach((it) => {
      const enumItem = new EnumItem();
      enumItem.rootPrototype = parentPrototype;
      enumItem.targetProperty = propertyKey + '.' + it.targetProperty;
      enumItem.sourceProperty = propertyKey + '.' + it.sourceProperty;
      enumItem.listGenerator = it.listGenerator;
      enumItem.defaultGenerator = it.defaultGenerator;
      FeaturesImpl.enumItems.push(enumItem);
    });
  FeaturesImpl.stringItems
    .filter((it) => it.rootPrototype === childPrototype)
    .forEach((it) => {
      const stringItem = new StringItem();
      stringItem.rootPrototype = parentPrototype;
      stringItem.targetProperty = propertyKey + '.' + it.targetProperty;
      stringItem.multiline = it.multiline;
      stringItem.readonly = it.readonly;
      stringItem.uppercase = it.uppercase;
      FeaturesImpl.stringItems.push(stringItem);
    });
  FeaturesImpl.calculators
    .filter((it) => it.rootPrototype === childPrototype)
    .forEach((it) => {
      const calc = new Calculator();
      calc.calculator = it.calculator;
      calc.pathPrefix = propertyKey + '.' + it.pathPrefix;
      calc.paths = [];
      it.paths.forEach((p) => calc.paths.push(propertyKey + '.' + p));
      calc.targetProperty = propertyKey + '.' + it.targetProperty;
      calc.rootPrototype = parentPrototype;
      FeaturesImpl.calculators.push(calc);
    });
}

export function _(target: any, propertyKey: string) {
  const metadata = Reflect.getMetadata('design:type', target, propertyKey);
  if (metadata.name === 'Object') {
    throw new Error(
      'Invalid property type definition. Inferred types not supported, please use explicit types. Property key: ' + propertyKey,
    );
  }
  let items = FeaturesImpl.tree.get(target);
  if (!items) {
    items = [];
    FeaturesImpl.tree.set(target, items);
  }
  items.push({ prot: metadata.prototype, prop: propertyKey });
  FeaturesImpl.deferredMetadata.push({ parentPrototype: target, propertyKey, childPrototype: metadata.prototype });
}

export class FingerprintBlob {
  scanned = 0;

  static deserialize(template: FingerprintBlob, source: any): FingerprintBlob {
    template.scanned = source.scanned;
    return template;
  }
}

export class FingerprintsBlob {
  hash = '';
  cert = '';
  ekey = '';
  digestURN = '';
  transportURN = '';
  items: Array<FingerprintBlob> = new Array<FingerprintBlob>();

  constructor() {
    for (let i = 0; i < 10; i++) {
      this.items.push(new FingerprintBlob());
    }
  }

  static deserialize(template: FingerprintsBlob, source: any): FingerprintsBlob {
    template.hash = source.hash;
    template.cert = source.cert;
    template.ekey = source.ekey;
    template.digestURN = source.digestURN;
    template.transportURN = source.transportURN;
    for (let i = 0; i < 10; ++i) {
      template.items[i] = FingerprintBlob.deserialize(template.items[i], source.items[i]);
    }
    return template;
  }
}

export class NullableString {
  value: string | null = '';

  static deserialize(template: NullableString, source: any): NullableString {
    template.value = source.value;
    return template;
  }

  static wrap(val: string | null): NullableString {
    const result = new NullableString();
    result.value = val;
    return result;
  }

  static unwrap(val: NullableString): string | null {
    return val.value;
  }
}

export class FeatureArray<T> extends Array<T> {
  readonly template: T;
  readonly resourceKeyPrefix: string;
  constructor(t: (new () => T) | number, items: Array<T>) {
    if (typeof t === 'number') {
      // https://stackoverflow.com/a/59665806
      super(t);
    } else {
      super(...items);
      this.template = new t();
      this.resourceKeyPrefix = 'ARRAY.' + t.name + '.';
    }
  }
}

function findFeatureArrayItemPrototype(model: any, parentPrototype: any, arrayProperty: string): any {
  if (Object.getPrototypeOf(model) === parentPrototype) {
    return Object.getPrototypeOf(model[arrayProperty].template);
  }
  const children = FeaturesImpl.tree.get(Object.getPrototypeOf(model));
  if (children !== undefined) {
    for (const child of children) {
      let subModel = model[child.prop];
      if (child.prop.constructor.name === 'FeatureArray') {
        subModel = subModel.template;
      }
      const result = findFeatureArrayItemPrototype(subModel, parentPrototype, arrayProperty);
      if (result !== null) {
        return result;
      }
    }
  }
  return null;
}

function findFeatureArrayItemPrototypeEx(allFeatures: any[], parentPrototype: any, arrayProperty: string): any {
  for (const features of allFeatures) {
    const result = findFeatureArrayItemPrototype(features, parentPrototype, arrayProperty);
    if (result !== null) {
      return result;
    }
  }
  throw new Error('Metadata error');
}

export interface IFormattingOptions {
  dateFormatter: (date: MaskedDate | NullableMaskedDate, miss?: string) => string;
}

export class Features {
  private static transliterator = new Transliterator();

  static processDeferredMetadata(allFeatures: any[]) {
    for (const x of FeaturesImpl.deferredMetadata) {
      if (x.childPrototype.constructor.name === 'FeatureArray') {
        reflect(
          x.parentPrototype,
          x.propertyKey + '.template',
          findFeatureArrayItemPrototypeEx(allFeatures, x.parentPrototype, x.propertyKey),
        );
      } else {
        reflect(x.parentPrototype, x.propertyKey, x.childPrototype);
      }
    }
    FeaturesImpl.deferredMetadata.length = 0;
  }

  static visibleField<T>(t: new () => T, targetPropertyLambda: (_: T) => any, targetVisibilityLambda: (_: T) => boolean) {
    const result = new VisibleItem();
    result.rootPrototype = t.prototype;
    result.nodePrototype = t.prototype;
    result.targetProperty = getPath(targetPropertyLambda);
    result.targetVisibilityLambda = targetVisibilityLambda;
    FeaturesImpl.visibleItems.push(result);
  }

  static maskedDateOptions<T>(
    t: new () => T,
    targetPropertyLambda: (_: T) => any,
    dmy: boolean,
    my: boolean,
    y: boolean,
    maxDate: (() => UTCDate) | null,
  ) {
    const result = new MaskedDateItem();
    result.rootPrototype = t.prototype;
    result.targetProperty = getPath(targetPropertyLambda);
    result.dmy = dmy;
    result.my = my;
    result.y = y;
    result.maxDate = maxDate;
    FeaturesImpl.maskedDateItems.push(result);
  }

  /**
   * Установить опции для перечислимого поля.
   * @param t Тип T, в котором находится перечислимое поле.
   * @param targetPropertyLambda Лямбда-выражение с использованием dot notation, извлекающее перечислимое поле из объекта типа T.
   * @param sourcePropertyLambda Лямбда-выражение с использованием dot notation, извлекающее "мастер" поле (поле, за изменениями в котором надо следить) из объекта типа T.
   * @param listGenerator Генератор возможных значений перечислимого поля. Перевызывается при изменениях в "мастер" поле. Принимает на вход модель целиком (недостаток, нужно принимать на вход объект типа T).
   * @param defaultGenerator Генератор умолчательного значения перечислимого поля. Перевызывается при изменениях в "мастер" поле. Принимает на вход модель целиком (недостаток, нужно принимать на вход объект типа T).
   */
  static enumOptions<T>(
    t: new () => T,
    targetPropertyLambda: (_: T) => any,
    sourcePropertyLambda: (_: T) => any,
    listGenerator: (root: any) => Enum[],
    defaultGenerator: (root: any) => Enum | null,
  ) {
    const result = new EnumItem();
    result.rootPrototype = t.prototype;
    result.targetProperty = getPath(targetPropertyLambda);
    result.sourceProperty = getPath(sourcePropertyLambda);
    result.listGenerator = listGenerator;
    result.defaultGenerator = defaultGenerator;
    FeaturesImpl.enumItems.push(result);
  }

  static stringOptions<T>(t: new () => T, targetPropertyLambda: (_: T) => any, multiline: boolean, uppercase: boolean, readonly: boolean) {
    const result = new StringItem();
    result.rootPrototype = t.prototype;
    result.targetProperty = getPath(targetPropertyLambda);
    result.multiline = multiline;
    result.uppercase = uppercase;
    result.readonly = readonly;
    FeaturesImpl.stringItems.push(result);
  }

  static defaultValue<T>(t: new () => T, targetPropertyLambda: (_: T) => any, calculator: Calculator<T>) {
    calculator.targetProperty = getPath(targetPropertyLambda);
    calculator.rootPrototype = t.prototype;
    FeaturesImpl.calculators.push(calculator);
  }

  static transliterate<T>(pathLambda: (roots: T) => string): Calculator<T> {
    const result = new Calculator<T>();
    result.pathPrefix = '';
    result.paths = [getPath(pathLambda)];
    result.calculator = (root, pathPrefix, paths) =>
      Features.transliterator.transliterateLastName(
        paths[0].split('.').reduce((pv, cv) => pv[cv], root),
        TransliterationMethod.IKAO,
      );
    return result;
  }

  static copyIf<T>(pathLambda: (roots: T) => any, conditionLambda: (roots: T) => boolean): Calculator<T> {
    const result = new Calculator<T>();
    result.ignoreNulls = true;
    result.pathPrefix = '';
    result.paths = [getPath(pathLambda)];
    result.calculator = (root, pathPrefix, paths) => {
      const it = pathPrefix ? pathPrefix.split('.').reduce((pv, cv) => pv[cv], root) : root;
      if (conditionLambda(it)) {
        const sourceValue = paths[0].split('.').reduce((pv, cv) => pv[cv], root);
        if (typeof sourceValue === 'object') {
          const clonedValue = new sourceValue.constructor();
          FeaturesImpl.readTree(JSON.parse(JSON.stringify(sourceValue)), clonedValue);
          return clonedValue;
        }
        return sourceValue;
      }
      return null;
    };
    return result;
  }

  static birthPlaceDefaults<T>(
    fullBirthplacePathLambda: (_: T) => string,
    partialBirthPlacePathLambda: (_: T) => string,
    birthdayPathLambda: (_: T) => MaskedDate,
    calculatorFunc: (full: string, partial: string, birthday: MaskedDate) => string | null,
  ): Calculator<T> {
    const result = new Calculator<T>();
    result.ignoreNulls = true;
    result.pathPrefix = '';
    result.paths = [getPath(fullBirthplacePathLambda), getPath(partialBirthPlacePathLambda), getPath(birthdayPathLambda)];
    result.calculator = (root, pathPrefix, paths) =>
      calculatorFunc(
        paths[0].split('.').reduce((pv, cv) => pv[cv], root),
        paths[1].split('.').reduce((pv, cv) => pv[cv], root),
        paths[2].split('.').reduce((pv, cv) => pv[cv], root),
      );
    return result;
  }

  static today(date: Date) {
    const result = new MaskedDate();
    result.value = UTCDate.today(date).toJSON().split('T')[0];
    return result;
  }

  static ageBetween(
    refDate: MaskedDate | NullableMaskedDate,
    roundRefUp: boolean,
    birthDate: MaskedDate | NullableMaskedDate,
    roundBirthUp: boolean,
    ageBegin: number | null,
    ageEnd: number | null,
    defaultResult: boolean,
  ): boolean {
    const refObj = refDate ? UTCDate.iso(refDate.value, roundRefUp) : null;
    const birthObj = birthDate ? UTCDate.iso(birthDate.value, roundBirthUp) : null;
    if (refObj === null || birthObj === null) {
      return defaultResult;
    }
    return (ageBegin === null || birthObj.addYears(ageBegin) <= refObj) && (ageEnd === null || birthObj.addYears(ageEnd) > refObj);
  }

  @memoize({ max: 64 })
  private static doSimpleSuggestion(source: Observable<any>, prefix: string): Observable<string[]> {
    return source.pipe(
      filter((v) => v.startsWith(prefix)),
      toArray(),
    );
  }

  static simpleSuggestion(source: Observable<any>, prefix: string): Observable<string[]> {
    return Features.doSimpleSuggestion(source, prefix);
  }

  @memoize({ max: 64 })
  private static doGenderedSuggestion(source: Observable<any>, gender: any, prefix: string): Observable<string[]> {
    return source.pipe(
      filter((v) => v.gender === gender),
      map((v) => v.value),
      filter((v) => v.startsWith(prefix)),
      toArray(),
    );
  }

  static genderedSuggestion<T>(source: Observable<any>, root: T, genderExtractor: (_: T) => any, prefix: string): Observable<string[]> {
    return Features.doGenderedSuggestion(source, genderExtractor(root), prefix);
  }

  @memoize({ max: 64 })
  private static doBirthplaceSuggestion(
    source: Observable<any>,
    birthdayStr: string,
    birthplaceValueExtractor: (_: any) => string,
    prefix: string,
  ): Observable<string[]> {
    const birthdayDn = UTCDate.iso(birthdayStr, false);
    const birthdayUp = UTCDate.iso(birthdayStr, true);
    return source.pipe(
      filter(
        (v) =>
          birthdayDn === null ||
          birthdayUp === null ||
          ((v.begin === null || birthdayUp >= v.begin) && (v.end === null || birthdayDn < v.end)),
      ),
      map((v) => birthplaceValueExtractor(v)),
      filter((v) => !!v && v.startsWith(prefix)),
      distinct(),
      toArray(),
    );
  }

  static birthplaceSuggestion<T>(
    source: Observable<any>,
    root: T,
    birthdayExtractor: (_: T, arrayIndexes: number[]) => MaskedDate,
    birthplaceValueExtractor: (_: any) => string,
    prefix: string,
    arrayIndexes: number[],
  ): Observable<string[]> {
    return Features.doBirthplaceSuggestion(source, birthdayExtractor(root, arrayIndexes).value, birthplaceValueExtractor, prefix);
  }

  @memoize({ max: 64 })
  private static doMySuggestionKLADR(source: (prefix: string) => Observable<string>, prefix: string): Observable<string[]> {
    return source(prefix).pipe(toArray());
  }

  static mySuggestionKLADR(source: (prefix: string) => Observable<string>, prefix: string): Observable<string[]> {
    return Features.doMySuggestionKLADR(source, prefix);
  }

  static suggestedOptions<T>(
    t: new () => T,
    modelPropertyPathLambda: (_: T) => string | NullableString,
    optionsLambda: (_: T, prefix: string, arrayIndexes: number[]) => Observable<string[]>,
  ) {
    FeaturesImpl.dicts[getPath(modelPropertyPathLambda)] = optionsLambda;
  }

  static columns<T>(
    t: new () => T,
    cortegeName: string,
    targets: Array<{ prop: (_: T) => any; viewPixelSize: number; searchable: boolean; editPercentSize: number }>,
  ) {
    targets.forEach((target) => {
      const column = new Column<T>();
      column.rootPrototype = t.prototype;
      column.cortegeName = cortegeName;
      column.viewPixelSize = target.viewPixelSize;
      column.targetProperty = getPath(target.prop);
      column.searchable = target.searchable;
      column.editPercentSize = target.editPercentSize;
      FeaturesImpl.columns.push(column);
    });
  }

  static arrayAppereance<T>(
    t: new () => T,
    compare: ((recordA: T, recordB: T) => number) | undefined,
    onEditClosed: ((record: T, arr: T[]) => T | null) | undefined,
    editPixelSizes: Map<number, number[]>,
    propertyAppereances: Array<{
      prop: (_: T) => any;
      viewFractionSize: number;
      minTableWidthVisible?: number;
      setters?: ArrayColumnSetter<T>[];
      compact?: boolean;
    }>,
    mobileItemAppereance: {
      firstLineFormatter?: (record: any, options: IFormattingOptions) => string;
      secondLineFormatter?: (record: any, options: IFormattingOptions) => string;
    },
    dateRanges: Array<{ propFrom: (_: T) => any; propTo: (_: T) => any }>,
  ) {
    const arrayInfo: ArrayInfo = {
      rootPrototype: t.prototype,
      compare: compare,
      onEditClosed: onEditClosed,
      dateRanges: [],
    };
    dateRanges.forEach((range) => {
      arrayInfo.dateRanges.push({ propFrom: getPath(range.propFrom), propTo: getPath(range.propTo) });
    });
    FeaturesImpl.arrayInfos.push(arrayInfo);
    propertyAppereances.forEach((appereance) => {
      FeaturesImpl.arrayColumnInfos.push({
        rootPrototype: t.prototype,
        prop: getPath(appereance.prop),
        viewFractionSize: appereance.viewFractionSize,
        minTableWidthVisible: appereance.minTableWidthVisible || 0,
        setters: appereance.setters || [],
        compact: appereance.compact ?? false
      });
    });
    FeaturesImpl.mobileArrayInfos.push({
      rootPrototype: t.prototype,
      firstLineFormatter: mobileItemAppereance.firstLineFormatter,
      secondLineFormatter: mobileItemAppereance.secondLineFormatter,
    });
    editPixelSizes.forEach((v, k) => {
      FeaturesImpl.arrayColumnEditInfos.push({
        rootPrototype: t.prototype,
        minWidth: k,
        editPixelSizes: v,
      });
    });
  }

  static documentField<T>(t: new () => T, field: DocumentField, targetPropertyLambda: ((_: T) => any) | string) {
    const result = new DocumentFieldInfo();
    result.rootPrototype = t.prototype;
    result.field = field;
    if (typeof targetPropertyLambda === 'string') {
      result.targetProperty = targetPropertyLambda;
      result.targetPropertyIsConstant = true;
    } else {
      result.targetProperty = getPath(targetPropertyLambda);
      result.targetPropertyIsConstant = false;
    }
    FeaturesImpl.documentFields.push(result);
  }

  static resetEnumFields(rootModel: any, rootTemplate: any, eventSourcePath?: string) {
    FeaturesImpl.enumItems
      .filter((it) => it.rootPrototype === Object.getPrototypeOf(rootModel))
      .forEach((it) => {
        const path = it.targetProperty.split('.');
        const value = path.reduce((pv, cv) => (pv === undefined ? undefined : pv[cv]), rootModel);
        if (value !== undefined) {
          const defaultValue = it.defaultGenerator(rootModel);
          if (defaultValue !== null) {
            const values = it.listGenerator(rootModel);
            if (!value.value || !values.includes(value) || (eventSourcePath === it.sourceProperty && value !== defaultValue)) {
              const it2 = path.slice(0, -1).reduce((pv, cv) => pv[cv], rootModel);
              it2[path.slice(-1)[0]] = defaultValue;
            }
          }
        }
      });
  }

  static updateFieldsVisibility(rootModel: any, rootTemplate: any) {
    FeaturesImpl.visibleItems
      .filter((it) => it.rootPrototype === Object.getPrototypeOf(rootModel))
      .forEach((it) => {
        const path = it.targetProperty.split('.');
        const targetTemplate = FeaturesImpl.makeTemplate(Object.getPrototypeOf(rootTemplate), rootTemplate, path);
        FeaturesImpl.showField(
          Object.getPrototypeOf(rootModel),
          rootModel,
          path,
          it.nodePrototype,
          null,
          it.targetVisibilityLambda,
          targetTemplate,
        );
      });
  }

  static registerValidationSpecification<T>(
    t: new () => T,
    additionalRules: any,
    specification: any,
    warnings: ValidationSeveritySetting[],
    notifications: ValidationSeveritySetting[],
  ) {
    const v = new ValidationSpecification();
    v.rootPrototype = t.prototype;
    v.additionalRules = additionalRules;
    v.specification = specification;
    v.warnings = warnings;
    v.notifications = notifications;
    FeaturesImpl.validationSpecifications.push(v);
  }

  static keyboardLanguage<T>(t: new () => T, targetPropertyLambda: (_: T) => any, lang: 'ru' | 'en' | null) {
    const propertyPath = getPath(targetPropertyLambda);
    FeaturesImpl.keyboardLanguages[propertyPath] = lang;
  }

  static getKeyboardLanguage(path: string): 'en' | 'ru' | null {
    return FeaturesImpl.keyboardLanguages[path] || 'ru';
  }

  static parseMaskedDate(value: string | null, parts: 'Y' | 'MY' | 'DMY' | null, locale: string): string | null {
    if (value === null || value === '') {
      return value;
    }

    const x = new MaskedDateItem<any>();
    x.y = parts === null || parts === 'Y';
    x.my = parts === null || parts === 'MY';
    x.dmy = parts === null || parts === 'DMY';
    return x.normalize(value, locale);
  }

  static formatMaskedDate(value: string | null, part: 'D' | 'M' | 'Y' | null, locale: string): string | null {
    return FeaturesImpl.formatMaskedDate(part, value, locale);
  }
}
